import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useMusicPlayer } from '../MusicPlayerContext';
import { getCoverImageUrl, hasVideo } from '../utils/musicStreamUrls';
import { isInstrumentalMusic } from '../utils/musicMeta';
import { showUiHint } from '../utils/uiHint';
import { theme, buttonClass } from '../ui/theme';

const PlaylistManager = () => {
  const { t, i18n } = useTranslation();
  const { setPlaylist, setCurrentMusic, setPlaying } = useMusicPlayer();
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  // 删除确认弹窗
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, playlist: null });
  // 手风琴展开与详情缓存（懒加载）
  const [expandedId, setExpandedId] = useState(null);
  const [detailsById, setDetailsById] = useState({}); // { [id]: { loading, error, musics, music_ids } }
  // 拖拽排序
  const [dragState, setDragState] = useState({ playlistId: null, fromIndex: -1 });
  // 添加歌曲弹窗（从音乐库搜索添加）
  const [addSongsModal, setAddSongsModal] = useState({ open: false, playlistId: null });
  const [addSongsQuery, setAddSongsQuery] = useState('');
  const [addSongsResults, setAddSongsResults] = useState([]);
  const [addSongsLoading, setAddSongsLoading] = useState(false);
  const [addSongsError, setAddSongsError] = useState(null);

  // 获取用户ID
  const getUserId = useCallback(() => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'user_id') return value;
    }
    const userId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    document.cookie = `user_id=${userId}; path=/; max-age=31536000`;
    return userId;
  }, []);

  const fetchPlaylists = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const userId = getUserId();
      // 添加时间戳参数来绕过 Service Worker 缓存
      const response = await axios.get('/api/music/playlist', {
        headers: {
          'X-User-ID': userId,
          'Cache-Control': 'no-cache' // 禁用 HTTP 缓存
        },
        params: {
          _t: Date.now() // 时间戳参数，确保每次都是新请求
        }
      });
      
      if (response.data.success) {
        setPlaylists(response.data.data.playlists || []);
      } else {
        setError(response.data.error || t('playlistManager.fetchFailed'));
      }
    } catch (err) {
      console.error('Fetch playlists error:', err);
      setError(err.response?.data?.error || t('playlistManager.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [getUserId, t]);

  useEffect(() => {
    fetchPlaylists();
  }, [fetchPlaylists]);

  const getCurrentLanguageTitle = useCallback((music) => {
    const lang = i18n?.language || 'cn';
    switch (lang) {
      case 'cn':
        return music.title_zh_cn || music.title;
      case 'tw':
        return music.title_zh_tw || music.title;
      case 'ja':
        return music.title_ja || music.title;
      case 'en':
        return music.title_en || music.title;
      default:
        return music.title;
    }
  }, [i18n?.language]);

  const formatDuration = useCallback((seconds) => {
    if (!seconds) return '--:--';
    const s = Number(seconds);
    if (!Number.isFinite(s) || s <= 0) return '--:--';
    const mins = Math.floor(s / 60);
    const secs = Math.floor(s % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const fetchPlaylistDetail = useCallback(async (playlistId) => {
    setDetailsById(prev => ({
      ...prev,
      [playlistId]: {
        ...(prev[playlistId] || {}),
        loading: true,
        error: null
      }
    }));
    try {
      const userId = getUserId();
      const resp = await axios.get(`/api/music/playlist/${playlistId}`, {
        headers: { 'X-User-ID': userId, 'Cache-Control': 'no-cache' },
        params: { _t: Date.now() }
      });
      if (resp?.data?.success) {
        const pl = resp.data.data || {};
        const musics = Array.isArray(pl.musics) ? pl.musics.filter(Boolean) : [];
        const music_ids = Array.isArray(pl.music_ids) ? pl.music_ids : (Array.isArray(pl.music_ids) ? pl.music_ids : []);
        setDetailsById(prev => ({
          ...prev,
          [playlistId]: { loading: false, error: null, musics, music_ids }
        }));
      } else {
        setDetailsById(prev => ({
          ...prev,
          [playlistId]: { ...(prev[playlistId] || {}), loading: false, error: resp?.data?.error || t('playlistManager.loadSongsFailed') }
        }));
      }
    } catch (err) {
      console.error('Fetch playlist detail error:', err);
      setDetailsById(prev => ({
        ...prev,
        [playlistId]: { ...(prev[playlistId] || {}), loading: false, error: err?.response?.data?.error || t('playlistManager.loadSongsFailed') }
      }));
    }
  }, [getUserId, t]);

  const toggleExpand = useCallback(async (playlistId) => {
    setExpandedId(prev => (prev === playlistId ? null : playlistId));
    const detail = detailsById[playlistId];
    const alreadyLoaded = detail && Array.isArray(detail.musics);
    if (!alreadyLoaded) {
      await fetchPlaylistDetail(playlistId);
    }
  }, [detailsById, fetchPlaylistDetail]);

  const savePlaylistMusicIds = useCallback(async (playlistId, music_ids) => {
    const userId = getUserId();
    return await axios.put(`/api/music/playlist/${playlistId}`, { music_ids }, {
      headers: { 'X-User-ID': userId, 'Content-Type': 'application/json' }
    });
  }, [getUserId]);

  const updatePlaylistCountLocal = useCallback((playlistId, music_ids) => {
    setPlaylists(prev => prev.map(p => (p.id === playlistId ? { ...p, music_ids } : p)));
  }, []);

  const handleRemoveSong = useCallback(async (playlistId, musicId, anchorEl) => {
    const detail = detailsById[playlistId];
    if (!detail || !Array.isArray(detail.musics)) return;
    const prevIds = Array.isArray(detail.music_ids) ? detail.music_ids : detail.musics.map(m => m.id).filter(Boolean);
    const nextIds = prevIds.filter(id => id !== musicId);
    const prevMusics = detail.musics;
    const nextMusics = prevMusics.filter(m => m && m.id !== musicId);

    // 乐观更新
    setDetailsById(prev => ({ ...prev, [playlistId]: { ...detail, musics: nextMusics, music_ids: nextIds } }));
    updatePlaylistCountLocal(playlistId, nextIds);

    try {
      const resp = await savePlaylistMusicIds(playlistId, nextIds);
      if (!(resp?.data?.success)) throw new Error(resp?.data?.error || 'save_failed');
      showUiHint(t('playlistManager.removeSongSuccess'), { anchorEl, duration: 1200 });
    } catch (err) {
      console.error('Remove song failed:', err);
      // 回滚
      setDetailsById(prev => ({ ...prev, [playlistId]: { ...detail, musics: prevMusics, music_ids: prevIds } }));
      updatePlaylistCountLocal(playlistId, prevIds);
      showUiHint(t('playlistManager.saveOrderFailed'), { anchorEl, duration: 1600 });
    }
  }, [detailsById, savePlaylistMusicIds, t, updatePlaylistCountLocal]);

  const handlePlayPlaylist = useCallback(async (playlistId) => {
    try {
      const userId = getUserId();
      const response = await axios.get(`/api/music/playlist/${playlistId}`, {
        headers: {
          'X-User-ID': userId
        },
        params: { _t: Date.now() }
      });

      if (response.data.success) {
        const pl = response.data.data;
        if (pl.musics && pl.musics.length > 0) {
          setPlaylist(pl.musics);
          setCurrentMusic(pl.musics[0]);
          setPlaying(true);
        }
      }
    } catch (err) {
      console.error('Play playlist error:', err);
      alert(err.response?.data?.error || t('playlistManager.playFailed'));
    }
  }, [getUserId, setPlaylist, setCurrentMusic, setPlaying, t]);

  const handlePlayAllFromDetail = useCallback(async (playlistId) => {
    const detail = detailsById[playlistId];
    if (detail && Array.isArray(detail.musics) && detail.musics.length > 0) {
      setPlaylist(detail.musics);
      setCurrentMusic(detail.musics[0]);
      setPlaying(true);
      return;
    }
    await handlePlayPlaylist(playlistId);
  }, [detailsById, setPlaylist, setCurrentMusic, setPlaying, handlePlayPlaylist]);

  const openAddSongsModal = useCallback((playlistId) => {
    setAddSongsModal({ open: true, playlistId });
    setAddSongsQuery('');
    setAddSongsResults([]);
    setAddSongsError(null);
  }, []);
  const closeAddSongsModal = useCallback(() => {
    setAddSongsModal({ open: false, playlistId: null });
    setAddSongsQuery('');
    setAddSongsResults([]);
    setAddSongsError(null);
    setAddSongsLoading(false);
  }, []);

  const openDeleteConfirm = useCallback((playlist) => {
    setDeleteConfirm({ open: true, playlist: playlist || null });
  }, []);
  const closeDeleteConfirm = useCallback(() => {
    setDeleteConfirm({ open: false, playlist: null });
  }, []);

  useEffect(() => {
    if (!deleteConfirm.open) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeDeleteConfirm();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [deleteConfirm.open, closeDeleteConfirm]);

  const searchSongs = useCallback(async (q) => {
    const query = String(q || '').trim();
    setAddSongsQuery(query);
    if (!query) {
      setAddSongsResults([]);
      setAddSongsError(null);
      return;
    }
    setAddSongsLoading(true);
    setAddSongsError(null);
    try {
      const resp = await axios.get('/api/music/list', {
        params: {
          search: query,
          page: 1,
          pageSize: 50,
          sort: 'latest'
        }
      });
      if (resp?.data?.success) {
        setAddSongsResults(Array.isArray(resp.data.data?.musics) ? resp.data.data.musics : []);
      } else {
        setAddSongsResults([]);
        setAddSongsError(resp?.data?.error || t('playlistManager.searchFailed'));
      }
    } catch (err) {
      console.error('Search songs error:', err);
      setAddSongsResults([]);
      setAddSongsError(err?.response?.data?.error || t('playlistManager.searchFailed'));
    } finally {
      setAddSongsLoading(false);
    }
  }, [t]);

  const handleAddSongToPlaylist = useCallback(async (playlistId, music, anchorEl) => {
    const detail = detailsById[playlistId];
    if (!detail || !Array.isArray(detail.musics)) return;
    const prevIds = Array.isArray(detail.music_ids) ? detail.music_ids : detail.musics.map(m => m.id).filter(Boolean);
    const mid = music?.id;
    if (!mid) return;
    if (prevIds.includes(mid)) {
      showUiHint(t('playlistManager.alreadyInPlaylist'), { anchorEl, duration: 1200, noWrap: true });
      return;
    }
    const nextIds = [...prevIds, mid];
    const prevMusics = detail.musics;
    const nextMusics = [...prevMusics, music];

    // 乐观更新
    setDetailsById(prev => ({ ...prev, [playlistId]: { ...detail, musics: nextMusics, music_ids: nextIds } }));
    updatePlaylistCountLocal(playlistId, nextIds);

    try {
      const resp = await savePlaylistMusicIds(playlistId, nextIds);
      if (!(resp?.data?.success)) throw new Error(resp?.data?.error || 'save_failed');
      showUiHint(t('playlistManager.addSuccess'), { anchorEl, duration: 1200 });
    } catch (err) {
      console.error('Add song failed:', err);
      // 回滚
      setDetailsById(prev => ({ ...prev, [playlistId]: { ...detail, musics: prevMusics, music_ids: prevIds } }));
      updatePlaylistCountLocal(playlistId, prevIds);
      showUiHint(t('playlistManager.saveOrderFailed'), { anchorEl, duration: 1600 });
    }
  }, [detailsById, savePlaylistMusicIds, t, updatePlaylistCountLocal]);

  const onDragStartSong = useCallback((playlistId, fromIndex) => {
    setDragState({ playlistId, fromIndex });
  }, []);
  const onDropSong = useCallback(async (playlistId, toIndex, anchorEl) => {
    const detail = detailsById[playlistId];
    if (!detail || !Array.isArray(detail.musics)) return;
    if (dragState.playlistId !== playlistId) return;
    const fromIndex = dragState.fromIndex;
    if (fromIndex < 0 || fromIndex === toIndex) return;

    const prevMusics = detail.musics;
    const nextMusics = prevMusics.slice();
    const [moved] = nextMusics.splice(fromIndex, 1);
    nextMusics.splice(toIndex, 0, moved);
    const nextIds = nextMusics.map(m => m.id).filter(Boolean);
    const prevIds = Array.isArray(detail.music_ids) ? detail.music_ids : prevMusics.map(m => m.id).filter(Boolean);

    // 乐观更新
    setDetailsById(prev => ({ ...prev, [playlistId]: { ...detail, musics: nextMusics, music_ids: nextIds } }));
    updatePlaylistCountLocal(playlistId, nextIds);

    try {
      const resp = await savePlaylistMusicIds(playlistId, nextIds);
      if (!(resp?.data?.success)) throw new Error(resp?.data?.error || 'save_failed');
    } catch (err) {
      console.error('Reorder save failed:', err);
      // 回滚
      setDetailsById(prev => ({ ...prev, [playlistId]: { ...detail, musics: prevMusics, music_ids: prevIds } }));
      updatePlaylistCountLocal(playlistId, prevIds);
      showUiHint(t('playlistManager.saveOrderFailed'), { anchorEl, duration: 1600 });
    } finally {
      setDragState({ playlistId: null, fromIndex: -1 });
    }
  }, [detailsById, dragState, savePlaylistMusicIds, t, updatePlaylistCountLocal]);

  const handleCreatePlaylist = async (playlistData) => {
    try {
      const userId = getUserId();
      const response = await axios.post('/api/music/playlist', playlistData, {
        headers: {
          'X-User-ID': userId,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setShowCreateForm(false);
        // 刷新播放列表
        await fetchPlaylists();
      } else {
        const errorMsg = response.data.error || response.data.message || t('playlistManager.createFailed');
        alert(errorMsg);
      }
    } catch (err) {
      console.error('Create playlist error:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message || t('playlistManager.createFailed');
      alert(errorMsg);
    }
  };

  // 清除 Service Worker 缓存中的播放列表相关请求
  const clearPlaylistCache = async () => {
    try {
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        const messageChannel = new MessageChannel();
        messageChannel.port1.onmessage = (event) => {
          if (event.data && event.data.ok) {
            console.log('[Playlist] Cache cleared');
          }
        };
        
        navigator.serviceWorker.controller.postMessage({
          type: 'CLEAR_API_CACHE',
          pattern: '/api/music/playlist'
        }, [messageChannel.port2]);
        
        // 等待响应（最多 1 秒）
        await new Promise((resolve) => {
          const timeout = setTimeout(resolve, 1000);
          messageChannel.port1.onmessage = () => {
            clearTimeout(timeout);
            resolve();
          };
        });
      }
    } catch (err) {
      console.warn('[Playlist] Failed to clear cache:', err);
    }
  };

  const deletePlaylistConfirmed = useCallback(async (playlistId) => {
    // 先立即从本地状态中移除，提供即时反馈
    setPlaylists(prev => prev.filter(p => p.id !== playlistId));

    try {
      const userId = getUserId();
      const response = await axios.delete(`/api/music/playlist/${playlistId}`, {
        headers: {
          'X-User-ID': userId
        }
      });

      if (response.data.success) {
        // 清除 Service Worker 缓存中的播放列表请求
        await clearPlaylistCache();
        // 删除成功，刷新列表确保数据同步
        await fetchPlaylists();
      } else {
        // 如果删除失败，恢复列表并显示错误
        await fetchPlaylists();
        alert(response.data.error || t('playlistManager.deleteFailed'));
      }
    } catch (err) {
      console.error('Delete playlist error:', err);
      
      // 如果是 404 错误（列表已不存在），说明删除已经成功（可能是并发删除）
      // 这种情况下不需要恢复列表，只需要刷新即可
      if (err.response?.status === 404) {
        await clearPlaylistCache();
        await fetchPlaylists();
        return;
      }
      
      // 其他错误：恢复列表并显示错误
      await fetchPlaylists();
      alert(err.response?.data?.error || t('playlistManager.deleteFailed'));
    }
  }, [fetchPlaylists, getUserId, t]);

  const handleConfirmDelete = useCallback(async () => {
    const playlistId = deleteConfirm.playlist?.id;
    if (!playlistId) {
      closeDeleteConfirm();
      return;
    }
    closeDeleteConfirm();
    await deletePlaylistConfirmed(playlistId);
  }, [closeDeleteConfirm, deleteConfirm.playlist, deletePlaylistConfirmed]);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-2 text-white/60">{t('playlistManager.loading')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">{t('playlistManager.title')}</h2>
        <button
          onClick={() => setShowCreateForm(true)}
          className={buttonClass({ variant: 'outline', size: 'xs' })}
        >
          {t('playlistManager.createPlaylist')}
        </button>
      </div>

      {error && (
        <div className={[theme.alerts.base, theme.alerts.variant.danger, 'px-2 py-1 text-xs'].join(' ')}>
          {error}
        </div>
      )}

      {showCreateForm && (
        <PlaylistForm
          onSave={handleCreatePlaylist}
          onCancel={() => setShowCreateForm(false)}
        />
      )}

      {/* 添加歌曲弹窗（从音乐库搜索添加） */}
      {addSongsModal.open && (
        <div className="fixed inset-0 z-[9999]" onMouseDown={closeAddSongsModal}>
          <div className="absolute inset-0 bg-black/25 backdrop-blur-[1px]" />
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{ width: '92vw', maxWidth: 720, minWidth: 280 }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className={[theme.surfaces.modalPanel, 'rounded-lg shadow-[0_22px_50px_rgba(0,0,0,0.22)] overflow-hidden text-white'].join(' ')}>
              <div className="px-3 py-2 flex items-center justify-between gap-2 border-b border-white/10 bg-white/[0.03]">
                <div className="text-xs font-semibold text-white">{t('playlistManager.addSongs')}</div>
                <button
                  type="button"
                  className="h-6 w-6 inline-flex items-center justify-center rounded-md bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 text-white/70"
                  onClick={closeAddSongsModal}
                  title={t('playlistManager.close')}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M6 18L18 6" />
                    <path d="M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-3">
                <input
                  type="text"
                  value={addSongsQuery}
                  onChange={(e) => searchSongs(e.target.value)}
                  placeholder={t('playlistManager.searchSongsPlaceholder')}
                  className={[theme.inputs.base, 'w-full px-2 py-1 text-xs rounded'].join(' ')}
                  autoFocus
                />
                <div className="mt-2 max-h-[52vh] overflow-auto border border-white/10 rounded">
                  {addSongsLoading ? (
                    <div className="px-3 py-2 text-xs text-white/55">{t('playlistManager.searching')}</div>
                  ) : addSongsError ? (
                    <div className="px-3 py-2 text-xs text-red-300">{addSongsError}</div>
                  ) : addSongsResults.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-white/55">{t('playlistManager.searchEmpty')}</div>
                  ) : (
                    addSongsResults.map((m) => (
                      <div key={m.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-white/[0.04]">
                        {getCoverImageUrl(m) ? (
                          <img
                            src={getCoverImageUrl(m)}
                            alt={getCurrentLanguageTitle(m)}
                            className="w-9 h-9 rounded object-cover flex-shrink-0"
                            loading="lazy"
                            decoding="async"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-9 h-9 rounded bg-white/[0.04] border border-white/10 flex-shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-medium text-white truncate inline-flex items-center gap-1">
                            {hasVideo(m) && (
                              <img
                                src="/icons/mtv-s.png"
                                alt="MTV"
                                className="w-4 h-4 flex-shrink-0 object-contain"
                                title={t('player.hasVideo') || '视频'}
                              />
                            )}
                            {!hasVideo(m) && isInstrumentalMusic(m) && (
                              <img
                                src="/icons/mcd-s.png"
                                alt={t('musicList.categoryInstrumental', '纯音乐')}
                                className="w-4 h-4 flex-shrink-0 object-contain"
                                title={t('musicList.categoryInstrumental', '纯音乐')}
                              />
                            )}
                            <span className="truncate">{getCurrentLanguageTitle(m)}</span>
                          </div>
                          <div className="text-[11px] text-white/55 flex items-center gap-2">
                            <span>{formatDuration(m.duration)}</span>
                            {m.artist && <span className="truncate">{m.artist}</span>}
                          </div>
                        </div>
                        <button
                          type="button"
                          className={buttonClass({ variant: 'outline', size: 'xs' })}
                          onClick={(e) => handleAddSongToPlaylist(addSongsModal.playlistId, m, e.currentTarget)}
                        >
                          {t('playlistManager.add')}
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 删除确认弹窗 */}
      {deleteConfirm.open && (
        <div className="fixed inset-0 z-[9999]" onMouseDown={closeDeleteConfirm}>
          <div className="absolute inset-0 bg-black/35 backdrop-blur-[1px]" />
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{ width: '92vw', maxWidth: 520, minWidth: 280 }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className={[theme.surfaces.modalPanel, 'rounded-lg shadow-[0_22px_50px_rgba(0,0,0,0.22)] overflow-hidden text-white'].join(' ')}>
              <div className="px-3 py-2 flex items-center justify-between gap-2 border-b border-white/10 bg-white/[0.03]">
                <div className="text-xs font-semibold text-white">
                  {t('playlistManager.confirmDeleteTitle', t('playlistManager.delete'))}
                </div>
                <button
                  type="button"
                  className="h-6 w-6 inline-flex items-center justify-center rounded-md bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 text-white/70"
                  onClick={closeDeleteConfirm}
                  title={t('playlistManager.close')}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M6 18L18 6" />
                    <path d="M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-3">
                <div className="text-xs text-white/75 leading-relaxed">
                  <div>{t('hints.confirmDeletePlaylist')}</div>
                  {deleteConfirm.playlist?.name && (
                    <div className="mt-1 text-[11px] text-white/55">
                      {t('playlistManager.playlistName', '播放列表')}：<span className="font-medium text-white">{deleteConfirm.playlist.name}</span>
                    </div>
                  )}
                </div>

                <div className="mt-3 flex justify-end gap-2">
                  <button
                    type="button"
                    className={buttonClass({ variant: 'ghost', size: 'xs' })}
                    onClick={closeDeleteConfirm}
                  >
                    {t('playlistManager.cancel')}
                  </button>
                  <button
                    type="button"
                    className={buttonClass({ variant: 'danger', size: 'xs' })}
                    onClick={handleConfirmDelete}
                  >
                    {t('playlistManager.delete')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {playlists.length === 0 ? (
        <div className={[theme.surfaces.glassCard, 'rounded-lg shadow-lg p-4 text-center text-white'].join(' ')}>
          <svg className="w-12 h-12 mx-auto text-white/45 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
          </svg>
          <p className="text-sm text-white/70">{t('playlistManager.noPlaylists')}</p>
          <p className="text-xs text-white/55 mt-1">{t('playlistManager.noPlaylistsHint')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {playlists.map((playlist) => (
            <div
              key={playlist.id}
              className={[theme.surfaces.glassItem, 'rounded-lg shadow-md p-3 hover:shadow-lg transition-shadow cursor-pointer text-white'].join(' ')}
              role="button"
              tabIndex={0}
              onClick={() => toggleExpand(playlist.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleExpand(playlist.id);
                }
              }}
            >
              <h3 className="font-semibold text-sm text-white mb-1">
                {playlist.name}
              </h3>
              {playlist.description && (
                <p className="text-xs text-white/70 mb-2 line-clamp-2">
                  {playlist.description}
                </p>
              )}
              <div className="flex items-center justify-between text-xs text-white/55 mb-2">
                <span>{t('playlistManager.musicCount', { count: playlist.music_ids?.length || 0 })}</span>
                {playlist.is_public && (
                  <span className="px-1.5 py-0.5 bg-emerald-500/10 border border-emerald-400/25 text-emerald-200 rounded text-xs">
                    {t('playlistManager.public')}
                  </span>
                )}
              </div>
              <div className="flex gap-1.5">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePlayPlaylist(playlist.id);
                  }}
                  className={['flex-1', buttonClass({ variant: 'outline', size: 'xs' })].join(' ')}
                >
                  {t('playlistManager.play')}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(playlist.id);
                  }}
                  className={buttonClass({ variant: 'ghost', size: 'xs' })}
                  title={expandedId === playlist.id ? t('playlistManager.collapse') : t('playlistManager.expand')}
                >
                  {expandedId === playlist.id ? t('playlistManager.collapse') : t('playlistManager.expand')}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingPlaylist(playlist);
                  }}
                  className={buttonClass({ variant: 'ghost', size: 'xs' })}
                >
                  {t('playlistManager.edit')}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openDeleteConfirm(playlist);
                  }}
                  className={buttonClass({ variant: 'danger', size: 'xs' })}
                >
                  {t('playlistManager.delete')}
                </button>
              </div>

              {/* 展开区：歌曲清单 */}
              {expandedId === playlist.id && (
                <div
                  className="mt-3 pt-2 border-t border-white/10 cursor-default"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="text-[11px] text-white/55">
                      {t('playlistManager.reorderHint')}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePlayAllFromDetail(playlist.id);
                        }}
                        className={buttonClass({ variant: 'outline', size: 'xs' })}
                      >
                        {t('playlistManager.playAll')}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openAddSongsModal(playlist.id);
                        }}
                        className={buttonClass({ variant: 'ghost', size: 'xs' })}
                      >
                        {t('playlistManager.addSongs')}
                      </button>
                    </div>
                  </div>

                  {(() => {
                    const d = detailsById[playlist.id];
                    if (!d || d.loading) {
                      return <div className="text-xs text-white/55 py-2">{t('playlistManager.loadingSongs')}</div>;
                    }
                    if (d.error) {
                      return (
                        <div className="text-xs text-red-300 py-2">
                          {String(d.error)}
                          <button
                            type="button"
                            className={['ml-2', theme.links.subtleUnderline].join(' ')}
                            onClick={(e) => {
                              e.stopPropagation();
                              fetchPlaylistDetail(playlist.id);
                            }}
                          >
                            {t('playlistManager.retryLoad')}
                          </button>
                        </div>
                      );
                    }
                    if (!d.musics || d.musics.length === 0) {
                      return <div className="text-xs text-white/55 py-2">{t('playlistManager.noSongsInPlaylist')}</div>;
                    }
                    return (
                      <div className="space-y-1">
                        {d.musics.map((m, idx) => (
                          <div
                            key={m.id || idx}
                            className="flex items-center gap-2 px-2 py-1 rounded hover:bg-white/[0.04]"
                            draggable
                            onDragStart={() => onDragStartSong(playlist.id, idx)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                              e.preventDefault();
                              onDropSong(playlist.id, idx, e.currentTarget);
                            }}
                            onDragEnd={() => setDragState({ playlistId: null, fromIndex: -1 })}
                            title={t('playlistManager.reorderHint')}
                          >
                            <div className="text-[11px] text-white/45 w-5 text-right flex-shrink-0">{idx + 1}</div>
                            {getCoverImageUrl(m) ? (
                              <img
                                src={getCoverImageUrl(m)}
                                alt={getCurrentLanguageTitle(m)}
                                className="w-8 h-8 rounded object-cover flex-shrink-0"
                                loading="lazy"
                                decoding="async"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            ) : (
                              <div className="w-8 h-8 rounded bg-white/[0.04] border border-white/10 flex-shrink-0" />
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="text-xs text-white truncate inline-flex items-center gap-1">
                                {hasVideo(m) && (
                                  <img
                                    src="/icons/mtv-s.png"
                                    alt="MTV"
                                    className="w-4 h-4 flex-shrink-0 object-contain"
                                    title={t('player.hasVideo') || '视频'}
                                  />
                                )}
                                {!hasVideo(m) && isInstrumentalMusic(m) && (
                                  <img
                                    src="/icons/mcd-s.png"
                                    alt={t('musicList.categoryInstrumental', '纯音乐')}
                                    className="w-4 h-4 flex-shrink-0 object-contain"
                                    title={t('musicList.categoryInstrumental', '纯音乐')}
                                  />
                                )}
                                <span className="truncate">{getCurrentLanguageTitle(m)}</span>
                              </div>
                              <div className="text-[11px] text-white/55 flex items-center gap-2">
                                <span>{formatDuration(m.duration)}</span>
                                {m.artist && <span className="truncate">{m.artist}</span>}
                              </div>
                            </div>
                            <button
                              type="button"
                              className={buttonClass({ variant: 'danger', size: 'xs' })}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveSong(playlist.id, m.id, e.currentTarget);
                              }}
                            >
                              {t('playlistManager.removeSong')}
                            </button>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editingPlaylist && (
        <PlaylistForm
          playlist={editingPlaylist}
          onSave={async (data) => {
            try {
              const userId = getUserId();
              const response = await axios.put(`/api/music/playlist/${editingPlaylist.id}`, data, {
                headers: {
                  'X-User-ID': userId,
                  'Content-Type': 'application/json'
                }
              });

              if (response.data.success) {
                setEditingPlaylist(null);
                fetchPlaylists();
              } else {
                alert(response.data.error || t('hints.playlistUpdateFailed'));
              }
            } catch (err) {
              console.error('Update playlist error:', err);
              alert(err.response?.data?.error || t('hints.playlistUpdateFailed'));
            }
          }}
          onCancel={() => setEditingPlaylist(null)}
        />
      )}
    </div>
  );
};

// 播放列表表单组件
const PlaylistForm = ({ playlist, onSave, onCancel }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: playlist?.name || '',
    description: playlist?.description || '',
    is_public: playlist?.is_public || false,
    music_ids: playlist?.music_ids || []
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert(t('hints.playlistNameRequired'));
      return;
    }
    onSave(formData);
  };

  return (
    <div className={[theme.surfaces.glassCard, 'rounded-lg shadow-lg p-4 text-white'].join(' ')}>
      <h3 className="text-lg font-semibold mb-3 text-white">
        {playlist ? t('playlistManager.editTitle') : t('playlistManager.createTitle')}
      </h3>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-white/80 mb-1">
            {t('playlistManager.name')} <span className="text-red-500">{t('playlistManager.nameRequired')}</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className={[theme.inputs.base, 'w-full px-1.5 py-0.5 text-xs rounded'].join(' ')}
            required
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-white/80 mb-1">
            {t('playlistManager.description')}
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows="3"
            className={[theme.inputs.base, 'w-full px-1.5 py-0.5 text-xs rounded'].join(' ')}
          />
        </div>
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              name="is_public"
              checked={formData.is_public}
              onChange={handleInputChange}
              className="mr-1.5 w-3 h-3"
            />
            <span className="text-xs text-white/75">{t('playlistManager.publicLabel')}</span>
          </label>
        </div>
        <div className="flex justify-end space-x-2">
          <button
            type="button"
            onClick={onCancel}
            className={buttonClass({ variant: 'ghost', size: 'xs' })}
          >
            {t('playlistManager.cancel')}
          </button>
          <button
            type="submit"
            className={buttonClass({ variant: 'outline', size: 'xs' })}
          >
            {playlist ? t('playlistManager.save') : t('playlistManager.create')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PlaylistManager;

